"use client"

import { useEffect, useState } from 'react'
import { Activity, Flame } from 'lucide-react'
import type { LivePulse } from '@/app/api/free-tier/live-pulse/route'

/**
 * THE LIVE VALUE MOMENT. Shown to a free user AT the moment their daily limit is reached - the point
 * where they have just experienced the product and the market is still firing without them.
 *
 * WHY THIS EXISTS (register `tasting-premium-is-the-real-lever-0819`). Returning users who never
 * tasted the real-time product convert at 0%; those who tasted convert at 10.9%. The old limit gate
 * converted on frustration ("come back tomorrow"). This converts on the value they can see continuing
 * in real numbers - the scanner firing NOW, named movers, live counts.
 *
 * IT DEGRADES TO NOTHING. If the pulse is empty or the fetch fails, this renders null and the gate's
 * existing generic copy stands. An enhancement, never a dependency.
 * IT NEVER LIES ABOUT TIME. `live` gates the "right now" language; a quiet tape says "recently".
 */
export default function LiveValueMoment() {
  const [pulse, setPulse] = useState<LivePulse | null>(null)

  useEffect(() => {
    let alive = true
    fetch('/api/free-tier/live-pulse')
      .then((r) => (r.ok ? r.json() : null))
      .then((d: LivePulse | null) => { if (alive && d && d.alertCount > 0) setPulse(d) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  if (!pulse || pulse.alertCount === 0) return null

  const nowText = pulse.live ? 'right now' : 'recently'

  return (
    <div className="mt-4 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-4">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-amber-300">
        <Activity className="h-3.5 w-3.5" />
        {pulse.live ? 'The scanner is live right now' : 'The scanner has been busy'}
      </div>
      <p className="mt-2 text-sm leading-relaxed text-zinc-200">
        <span className="font-bold text-white">{pulse.alertCount}</span> alerts on{' '}
        <span className="font-bold text-white">{pulse.symbolCount}</span> stocks {pulse.windowLabel}.
        {' '}You are seeing the delayed view.
      </p>
      {pulse.movers.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {pulse.movers.map((m) => (
            <span
              key={m.symbol}
              className="inline-flex items-center gap-1 rounded-lg border border-zinc-700/60 bg-zinc-900/60 px-2 py-1 text-[13px]"
            >
              <Flame className="h-3 w-3 text-amber-400" />
              <span className="font-extrabold text-amber-300">${m.symbol}</span>
              <span className="tabular-nums text-zinc-400">{m.rvol}x vol</span>
            </span>
          ))}
        </div>
      )}
      <p className="mt-3 text-xs text-zinc-500">
        Subscribers see every one of these the moment it fires{pulse.live ? ', live' : ''}.
      </p>
    </div>
  )
}
